import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import { AccountCircle as AccountCircleIcon } from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useFindProductUserNeedsByVerificationId,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  ProductUserNeed,
  BaseProductVerificationCardProps
} from '../Product.interface';
import {
  getProductEntityName,
  isProductRelationDisabled
} from '../Product.config';

export interface VerificationUserNeedReferenceCardProps
  extends BaseProductVerificationCardProps {
  fetchRef?: React.MutableRefObject<Function | undefined>;
}

const VerificationUserNeedReferenceCard: React.FC<VerificationUserNeedReferenceCardProps> =
  (props: VerificationUserNeedReferenceCardProps) => {
    const {
      productId,
      productVerification,
      productVerificationVersion,
      fetchRef,
      onError
    } = props;
    const history = useHistory();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productUserNeedList,
      isLoadingProductUserNeeds,
      loadingProductUserNeedsError,
      findProductUserNeeds
    } = useFindProductUserNeedsByVerificationId(
      productId,
      productVerification.id
    );
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.UserNeed
    );

    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };

    const findProductUserNeedsWithParams = useCallback(() => {
      const params: ApiParams = {};
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productVerificationVersion)
        params.version = productVerificationVersion.version;
      params.getFromRelation = !isProductRelationDisabled(
        'userNeed',
        'verifications'
      );
      findProductUserNeeds(params);
    }, [
      findProductUserNeeds,
      order,
      orderDirection,
      productVerificationVersion
    ]);

    useEffect(() => {
      if (fetchRef) {
        fetchRef.current = findProductUserNeedsWithParams;
      }
      findProductUserNeedsWithParams();
    }, [fetchRef, findProductUserNeedsWithParams]);

    useEffect(() => {
      if (loadingProductUserNeedsError) {
        onError?.(loadingProductUserNeedsError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingProductUserNeedsError]);

    return (
      <Card>
        <CardTitle>
          <AccountCircleIcon />
          {getProductEntityName('userNeed')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productUserNeed.id'}
                    direction={getSortDirection('productUserNeed.id')}
                    onClick={() => handleSort('productUserNeed.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productUserNeed.title'}
                    direction={getSortDirection('productUserNeed.title')}
                    onClick={() => handleSort('productUserNeed.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductUserNeeds && (
                <TableRow>
                  <TableCell colSpan={2} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productUserNeedList?.map((userNeed: ProductUserNeed) => (
                <TableRow
                  hover
                  key={userNeed.id}
                  onClick={() => {
                    history.push(
                      `/portal/products/${productId}/user-needs/${userNeed.id}`
                    );
                  }}
                >
                  <TableCell>{prefix + userNeed.id}</TableCell>
                  <WrappedTableCell>{userNeed.title}</WrappedTableCell>
                  <TableCell>
                    <TaskStatusLabel task={userNeed.task} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

export default VerificationUserNeedReferenceCard;
